import {
  findIndex,
  propEq,
  insert,
  pipe,
  propOr,
  curryN,
  pathEq,
  pathOr,
  find
} from 'ramda'

/**
 * Adds the KIDS nav item to the nav header and side for non-kids profiles or logged-out users
 * - Inserts before rental link, or before discover link if rental is absent
 * - Skips for authenticated kid profiles
 *
 * @param {boolean} isAuthenticated - Indicates whether the user is authenticated
 * @param {boolean} isKid - Indicates whether the user is a kid profile, or the current location path is `/kids` (determined by the `useIsKid` hook)
 * @param {Array} mainSectionLinks - The list of navigation links in the main section
 * @returns {Array} - Updated list of navigation links, including the KIDS link for non-kids or logged-out users
 */
export const addKidsLinkForNonKidProfiles = (isAuthenticated, isKid, mainSectionLinks) => {
  // The KIDS navigation item based on authentication status
  const kidsNavItem = {
    name: 'Kids',
    target: '40161d6d-8d34-4800-893b-ca702dec842e',
    type: 'SCREEN_ID',
    path: isAuthenticated ? '#' : '/kids',
    isRental: false,
    isDiscoverNav: false,
    children: [],
    isAuthenticated,
    isKids: true,
    __typename: 'NavItem'
  }

  // Determine insertion index for the KIDS menu
  const rentalIndex = findIndex(propEq('isRental', true), mainSectionLinks)
  const discoverIndex = findIndex(propEq('isDiscoverNav', true), mainSectionLinks)
  // Use the first available index or default to the end of the list
  let insertionIndex = mainSectionLinks.length
  if (rentalIndex >= 0) {
    insertionIndex = rentalIndex
  } else if (discoverIndex >= 0) {
    insertionIndex = discoverIndex
  }

  // Insert the KIDS menu
  const linksWithKids = insert(insertionIndex, kidsNavItem, mainSectionLinks)

  // Return original links for kids profiles
  if (isAuthenticated && isKid) return mainSectionLinks

  return linksWithKids
}

/**
 * Finds a section by name in the navigation
 * @param {string} name - The name of the section to find
 * @param {Array} nav - The navigation array, e.g. navFooter
 * @returns {Object|undefined} - The matching section or undefined
 */
export const findSection = curryN(2, (name, nav) => find(
  pathEq(['name'], name)
)(nav))

/**
 * Finds child links of a section by name
 * @param {string} name - The section name
 * @param {Array} nav - The navigation array, e.g. navFooter
 * @returns {Array} - An array of child links or an empty array
 */
export const findLinks = curryN(2, (name, nav) => pipe(
  findSection(name),
  pathOr([], ['children'])
)(nav))

/**
 * Extracts the main navigation links from the `navHeaderQuery` result
 * @param {Object} navHeaderQuery - Takes the result of the `navHeaderQuery` which should contain a `navHeader` property
 * @returns {Array} - Main navigation links from the `header.main` section
 */
export const getMainNavHeaderSectionLinks = pipe(
  propOr([], 'navHeader'),
  findLinks('header.main')
)

/**
 * Takes the main section links and returns the count of properties before the discover nav item
 * @param {Array} mainSectionLinks - The list of navigation links in the main section
 * @returns {number} - The index of the `discover nav` item, or -1 if not found
 */
export const getPropertiesCount = findIndex(propEq('isDiscoverNav', true))
